'use client';

// components/search/ProductGrid/SyfakProductCard.tsx
// The B2B (syfak) product card — mirrors syfakb2b.gr's own card: product code,
// live status tags, a "ΤΙΜΗ ΡΟΗΣ" price label, and an ALWAYS-VISIBLE price +
// offer/cart action, with FIXED-height sections so every card in a grid row
// lines up regardless of content (long title, tags or none, offer or not).
//
// Layout-only: ProductCard owns all the state, handlers and analytics and passes
// them in. Selected by ProductCard when ui.alwaysShowActions is set. Handles both
// the grid and list variants.

import React from 'react';
import type { ProductCardProps } from '../types';
import { QuantityInput } from './QuantityInput';
import type { NormalizedPrice } from '@/lib/pricing/externalPricing';
import { useClientConfig } from '@/lib/clients/ClientContext';
import { PriceDisplay, isPriceUnavailable } from './PriceDisplay';
import OfferPopup from '../OfferPopup';

/** One live status tag. `tone` comes from ui.pricingLabels; omitted = grey. */
export interface StatusTag {
  text: string;
  tone?: 'red' | 'blue' | 'grey';
}

/**
 * Tag palette — matched to syfak's own site, where a pharmacist scanning a list
 * relies on colour to spot the statuses that change what they can order.
 *
 * RED = the two "you cannot just order this" statuses (ΕΙΔΟΣ ΔΕΣΜΕΥΣΗΣ, ΕΙΔΟΣ ΜΕ
 * ΣΥΝΤΑΓΗ). BLUE = the ΕΛΛΕΙΠΤΙΚΟ family (short supply). GREY = everything else,
 * which is the long-standing look and stays the default.
 *
 * The blue is a readable shade of the SAME hue as the «ΠΟΛΥ ΠΕΡΙΟΡΙΣΜΕΝΗ
 * ΔΙΑΘΕΣΙΜΟΤΗΤΑ» availability badge (#a0d5ec). That exact value is a BACKGROUND
 * colour there — white bold text sits on top of it — and as text on white it
 * measures ~1.6:1 contrast, well under the 4.5:1 needed to be legible. Same
 * colour family, darkened until it can actually be read.
 */
const TAG_TONES: Record<'red' | 'blue' | 'grey', { text: string; border: string }> = {
  red: { text: '#C0392B', border: '#C0392B' },
  blue: { text: '#2B83AB', border: '#2B83AB' },
  grey: { text: '#000000', border: '#D8D8D8' },
};

function toneOf(tag: StatusTag) {
  return TAG_TONES[tag.tone ?? 'grey'];
}

// syfak month-offer button label. Gated on the customer price's offerUrl
// (syfak-only today); ProductCard imports this for its standard-card path too.
export const CHOOSE_OFFER_LABEL = 'ΕΠΙΛΕΞΕ ΠΡΟΣΦΟΡΑ';
// Post-add success label — the button flips to this for ~1.6s after a
// successful add (visual feedback; founder request 2026-07-27). Shared with
// ProductCard's standard-card path, same as CHOOSE_OFFER_LABEL.
export const ADDED_LABEL = '✓ Προστέθηκε';
// When the host reports the product is ALREADY in the basket (syfak's
// `quantity`), the box starts at that number and a click SETS the basket to the
// box — so the button says what it does (founder, 2026-10-01).
export const UPDATE_CART_LABEL = 'ΕΝΗΜΕΡΩΣΗ ΚΑΛΑΘΙΟΥ';
export const UPDATED_LABEL = '✓ Ενημερώθηκε';

function BasketIcon() {
  return (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
  );
}

function CheckIcon() {
  return (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
  );
}

function ImagePlaceholder({ className }: { className: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
  );
}

export interface SyfakProductCardProps {
  product: ProductCardProps['product'];
  variant: 'grid' | 'list';
  animate: boolean;
  index: number;
  imageUrl?: string;
  highlightedTitle: React.ReactNode;
  badge: { label: string; background: string; textColor: string } | null;
  statusTags: StatusTag[];
  customerPrice: NormalizedPrice | null | undefined;
  loadingCustomerPrice: boolean;
  customerPriceUnavailableLabel: string;
  cartButtonVisible: boolean;
  isOffer: boolean;
  offerUrl: string | null;
  offerOpen: boolean;
  quantity: number;
  cartAdded: boolean;
  /** «ΠΡΟΣΘΗΚΗ ΣΤΟ ΚΑΛΑΘΙ», or «ΕΝΗΜΕΡΩΣΗ ΚΑΛΑΘΙΟΥ» when already in the basket. */
  actionLabel: string;
  /** The ~1.6s confirmation after a successful click. */
  doneLabel: string;
  onProductClick: () => void;
  onAddToCart: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onOfferClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onDecrement: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onIncrement: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** A quantity typed into the box (whole number ≥ 1). */
  onSetQuantity: (quantity: number) => void;
  onOfferClose: () => void;
  onOfferCartAdd?: (quantity: number) => void;
}

export default function SyfakProductCard({
  product,
  variant,
  animate,
  index,
  imageUrl,
  highlightedTitle,
  badge,
  statusTags,
  customerPrice,
  loadingCustomerPrice,
  customerPriceUnavailableLabel,
  cartButtonVisible,
  isOffer,
  offerUrl,
  offerOpen,
  quantity,
  cartAdded,
  actionLabel,
  doneLabel,
  onProductClick,
  onAddToCart,
  onOfferClick,
  onDecrement,
  onIncrement,
  onSetQuantity,
  onOfferClose,
  onOfferCartAdd,
}: SyfakProductCardProps) {
  const clientConfig = useClientConfig();
  const linkTarget = clientConfig.ui.productLinkTarget ?? '_blank';
  const linkRel = linkTarget === '_blank' ? 'noopener noreferrer' : undefined;
  const codeLabel = clientConfig.ui.productCodeLabel;
  const offerLabel = clientConfig.ui.offerParticipationLabel;

  // «ΤΙΜΗ ΡΟΗΣ» is a heading for a price — so it is hidden when there is no
  // price to head. The endpoint answers [false,false] for products this customer
  // has no agreed price on, and the card then read «ΤΙΜΗ ΡΟΗΣ» directly above
  // «Επικοινωνήστε για τιμή», which syfak's team reported as the card
  // contradicting itself (2026-08). The condition is PriceDisplay's own, so the
  // heading cannot fall out of step with what renders beneath it.
  // While this card's price is loading we do not yet know what is already in
  // the basket (syfak returns the quantity with the price). Showing «1» there and
  // letting the button be pressed would SET the basket to 1 and cut the order —
  // so the box shows the same loading bar as the price and nothing is clickable
  // until the number arrives (founder, 2026-10-01).
  const qtyPending = loadingCustomerPrice;
  const qtyValue = qtyPending
    ? <span className="block w-4 h-3 bg-gray-200 animate-pulse rounded" aria-label="loading quantity" />
    : quantity;
  const priceLabel = isPriceUnavailable(loadingCustomerPrice, customerPrice)
    ? undefined
    : clientConfig.ui.priceLabel;

  const priceEl = (
    <PriceDisplay
      layout="horizontal"
      loading={loadingCustomerPrice}
      customerPrice={customerPrice}
      rrp={product.price ?? null}
      rrpOriginal={product.original_price ?? null}
      unavailableLabel={customerPriceUnavailableLabel}
    />
  );

  const popup = offerOpen && offerUrl && (
    <OfferPopup offerUrl={offerUrl} title={product.title} onClose={onOfferClose} onCartAdd={onOfferCartAdd} />
  );

  if (variant === 'list') {
    return (
      <div
        className={`flex items-stretch w-full bg-white border-b border-[#D8D8D8] ${animate ? 'product-enter' : ''}`}
        data-product-id={product.id}
        style={animate ? { animationDelay: `${Math.min(index * 30, 600)}ms` } : undefined}
      >
        {/* Image */}
        <a href={product.url} target={linkTarget} rel={linkRel} onClick={onProductClick} className="flex items-center justify-center w-[68px] md:w-[92px] p-2 md:p-3 flex-shrink-0 self-center cursor-pointer">
          {imageUrl ? (
            <img src={imageUrl} alt={product.title || 'Product'} className="w-[52px] h-[52px] md:w-[72px] md:h-[72px] object-contain" />
          ) : (
            <ImagePlaceholder className="w-9 h-9 text-gray-300" />
          )}
        </a>
        {/* Info + action */}
        <div className="flex-1 min-w-0 flex flex-col gap-[5px] py-3 px-4 border-l border-[#D8D8D8]">
          {badge && (
            <span className="self-start max-w-full px-2 py-[3px] text-[10px] font-gotham font-bold leading-[14px]" style={{ backgroundColor: badge.background, color: badge.textColor }}>{badge.label}</span>
          )}
          {codeLabel && product.model && (
            <p className="text-[11px] font-gotham font-normal text-gray-500 leading-normal">{codeLabel}: {product.model}</p>
          )}
          <a href={product.url} target={linkTarget} rel={linkRel} onClick={onProductClick} className="text-[13px] font-gotham font-bold text-black leading-snug line-clamp-2 hover:text-brand transition-colors cursor-pointer">{highlightedTitle}</a>
          {statusTags.length > 0 && (
            <div className="flex flex-wrap gap-[6px] w-full">
              {statusTags.map((t, i) => (
                <span
                  key={i}
                  // max-w-full + break-words: a tag must never push past the card.
                  // The free-text `message` label carries arbitrary host text and
                  // can be a full sentence, so it wraps inside the pill instead.
                  className="max-w-full px-2 py-[2px] text-[10px] font-gotham font-bold border rounded-[3px] leading-[14px] break-words"
                  style={{ color: toneOf(t).text, borderColor: toneOf(t).border }}
                >
                  {t.text}
                </span>
              ))}
            </div>
          )}
          {/* Price + action */}
          <div className="flex items-end justify-between gap-3 flex-wrap mt-1 pt-2 border-t-[0.5px] border-[#ECECEC]">
            <div className="flex flex-col gap-[2px]">
              {priceLabel && <span className="text-[11px] font-gotham font-bold text-brand tracking-[0.02em]">{priceLabel}</span>}
              {priceEl}
            </div>
            {cartButtonVisible && (isOffer ? (
              <div className="flex flex-col items-end gap-[4px]">
                {offerLabel && <span className="text-[11px] font-gotham font-bold text-[#d9534f]">{offerLabel}</span>}
                {/* No basket glyph: this button does NOT add to the cart, it
                    opens the tiered-offer popup (`setOfferOpen(true)`). The
                    basket was here to make the offer state look different from
                    plain add-to-cart, but it promised an action the button does
                    not perform — and the red «ΣΥΜΜΕΤΕΧΕΙ ΣΕ ΠΡΟΣΦΟΡΕΣ» label
                    directly above already marks the offer state. */}
                <button onClick={onOfferClick} className="flex items-center justify-center px-4 py-[9px] bg-brand hover:bg-brand-hover text-brand-text text-[12px] font-gotham font-bold whitespace-nowrap transition-colors cursor-pointer">
                  {CHOOSE_OFFER_LABEL}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex items-stretch h-9 border border-[#E2E2E2] rounded-[4px] overflow-hidden flex-shrink-0">
                  <button type="button" onClick={onDecrement} aria-label="Decrease quantity" className="w-8 flex items-center justify-center text-[16px] font-gotham text-black hover:bg-gray-50 disabled:opacity-40 disabled:cursor-wait cursor-pointer" disabled={qtyPending || quantity <= 1}>−</button>
                  {qtyPending
                    ? <span className="w-9 flex items-center justify-center text-[12px] font-gotham font-semibold text-black border-x border-[#E2E2E2]">{qtyValue}</span>
                    : <QuantityInput value={quantity} onCommit={onSetQuantity} maxDigits={4} className="w-9 text-[12px] font-gotham font-semibold text-black border-x border-[#E2E2E2]" />}
                  <button type="button" onClick={onIncrement} aria-label="Increase quantity" className="w-8 flex items-center justify-center text-[16px] font-gotham text-black hover:bg-gray-50 disabled:opacity-40 disabled:cursor-wait cursor-pointer" disabled={qtyPending}>+</button>
                </div>
                {/* Phone: a square icon-only button. The text version measures
                    187px, which together with the 102px stepper overflowed a
                    375px iPhone by 49px and was the sole cause of horizontal
                    scrolling in list view. The icon costs 36px instead.
                    From `md` up this is byte-for-byte the previous button. */}
                <button
                  onClick={onAddToCart}
                  disabled={qtyPending}
                  aria-label={cartAdded ? doneLabel : actionLabel}
                  title={cartAdded ? doneLabel : actionLabel}
                  className="h-9 w-9 md:w-auto px-0 md:px-4 gap-0 md:gap-2 flex items-center justify-center flex-shrink-0 bg-brand hover:bg-brand-hover disabled:opacity-50 disabled:hover:bg-brand disabled:cursor-wait text-brand-text text-[12px] font-gotham font-bold whitespace-nowrap transition-colors cursor-pointer"
                >
                  {cartAdded ? <CheckIcon /> : <BasketIcon />}
                  <span className="hidden md:inline">{cartAdded ? doneLabel : actionLabel}</span>
                </button>
              </div>
            ))}
          </div>
        </div>
        {popup}
      </div>
    );
  }

  // Grid variant
  return (
    // h-full + the mt-auto footer below: the card fills its grid cell, and the
    // price/action block is pinned to the BOTTOM. Grid rows stretch every cell to
    // the tallest card, so the buttons line up across a row no matter how many
    // status tags each product carries. (2026-07-28 — the tags were previously
    // squeezed into a fixed 44px so the footer would land at a fixed offset;
    // letting them grow then shifted the buttons, which is what the founder
    // caught. Bottom-pinning keeps BOTH: every tag visible, buttons aligned.)
    <div
      className={`bg-white flex flex-col h-full ${animate ? 'product-enter' : ''}`}
      data-product-id={product.id}
      style={animate ? { animationDelay: `${Math.min(index * 40, 600)}ms` } : undefined}
    >
      {/* Image — fixed height */}
      <a href={product.url} target={linkTarget} rel={linkRel} onClick={onProductClick} className="flex items-center justify-center w-full h-[150px] px-[10px] pt-3 flex-shrink-0 cursor-pointer">
        {imageUrl ? (
          <img src={imageUrl} alt={product.title || 'Product'} className="max-w-full max-h-full object-contain" />
        ) : (
          <ImagePlaceholder className="w-12 h-12 text-gray-300" />
        )}
      </a>

      {/* Info — availability/code/title keep RESERVED fixed heights so the TOP of
          every card lines up; only the tag area varies. pb-3 is the gap that used
          to be the footer's mt-3, which is now mt-auto. */}
      <div className="@container px-4 pt-3 pb-3 flex flex-col flex-shrink-0">
        {/* Availability (reserved). The label used to be one line + truncate,
            which cut «ΠΕΡΙΟΡΙΣΜΕΝΗ ΔΙΑΘΕΣΙΜΟΤΗΤΑ» (188px at 10px) at every
            grid width up to 1280px (slots measured 91–181px), and even
            «ΜΗΔΕΝΙΚΟ ΑΠΟΘΕΜΑ» (132px) on phones. Now it wraps to ≤2 lines, and
            when the card is narrower than the longest live label every card
            reserves 2 lines — all cards in a row share one width, so the row
            still lines up. Below 100px the longest single word
            («ΔΙΑΘΕΣΙΜΟΤΗΤΑ», ~99px) needs 9px + tighter padding to fit. */}
        <div className="h-[22px] @max-[188px]:h-[36px] flex items-center">
          {badge && (
            <span className="max-w-full line-clamp-2 break-words px-2 @max-[99px]:px-1.5 py-[3px] text-[10px] @max-[99px]:text-[9px] font-gotham font-bold leading-[14px]" style={{ backgroundColor: badge.background, color: badge.textColor }}>
              {badge.label}
            </span>
          )}
        </div>
        {/* Code (reserved) */}
        <div className="h-[15px] mt-[6px]">
          {codeLabel && product.model && (
            <p className="text-[11px] font-gotham font-normal text-gray-500 leading-none truncate">{codeLabel}: {product.model}</p>
          )}
        </div>
        {/* Title — fixed 2 lines */}
        <a
          href={product.url}
          target={linkTarget}
          rel={linkRel}
          onClick={onProductClick}
          className="block h-[36px] mt-[6px] text-[13px] font-gotham font-bold text-black leading-[18px] line-clamp-2 hover:text-brand transition-colors cursor-pointer"
        >
          {highlightedTitle}
        </a>
        {/* Status tags — RESERVED height, not a fixed one (2026-07-28).
            It was `h-[44px] overflow-hidden`, exactly two 19px rows: a third row
            was sliced in half, and a tag wider than the card (the free-text
            `message` can be a whole sentence) ran off the side and was cut. A
            status a pharmacist half-reads is worse than one that costs a few
            pixels of alignment, so the area now keeps its 44px MINIMUM — cards
            with 0–2 rows line up exactly as before — and grows when a product
            genuinely has more to say. */}
        <div className="min-h-[44px] mt-[8px] flex flex-wrap content-start gap-[6px]">
          {statusTags.map((t, i) => (
            <span
              key={i}
              className="min-h-[19px] max-w-full px-2 inline-flex items-center text-[10px] font-gotham font-bold border rounded-[3px] leading-[15px] break-words"
              style={{ color: toneOf(t).text, borderColor: toneOf(t).border }}
            >
              {t.text}
            </span>
          ))}
        </div>
      </div>

      {/* Price + action — mt-auto pins this block to the bottom of the card, and
          its internal heights stay fixed, so the price line, the action slot and
          the button align across every card in a row (offer or not, tags or not). */}
      <div className="border-t-[0.5px] border-[#D8D8D8] mt-auto mx-4 pt-3 pb-4 flex flex-col flex-shrink-0">
        {/* Price */}
        <div className="mb-[10px]">
          {priceLabel && <span className="block text-[11px] font-gotham font-bold text-brand tracking-[0.02em] leading-none mb-[5px]">{priceLabel}</span>}
          <div className="h-[20px] flex items-center">{priceEl}</div>
        </div>
        {cartButtonVisible && (
          <>
            {/* Reserved slot ABOVE the button: quantity stepper (non-offer) OR the
                "ΣΥΜΜΕΤΕΧΕΙ ΣΕ ΠΡΟΣΦΟΡΕΣ" line (offer) — same height either way, so
                the button always aligns. */}
            <div className="h-[36px] flex items-center mb-[8px]">
              {isOffer ? (
                offerLabel && <span className="text-[11px] font-gotham font-bold text-[#d9534f] leading-tight">{offerLabel}</span>
              ) : (
                <div className="flex items-stretch h-9 border border-[#E2E2E2] rounded-[4px] overflow-hidden">
                  <button type="button" onClick={onDecrement} aria-label="Decrease quantity" className="w-9 flex items-center justify-center text-[18px] font-gotham text-black hover:bg-gray-50 disabled:opacity-40 disabled:cursor-wait cursor-pointer" disabled={qtyPending || quantity <= 1}>−</button>
                  {qtyPending
                    ? <span className="w-10 flex items-center justify-center text-[13px] font-gotham font-semibold text-black border-x border-[#E2E2E2]">{qtyValue}</span>
                    : <QuantityInput value={quantity} onCommit={onSetQuantity} maxDigits={4} className="w-10 text-[13px] font-gotham font-semibold text-black border-x border-[#E2E2E2]" />}
                  <button type="button" onClick={onIncrement} aria-label="Increase quantity" className="w-9 flex items-center justify-center text-[18px] font-gotham text-black hover:bg-gray-50 disabled:opacity-40 disabled:cursor-wait cursor-pointer" disabled={qtyPending}>+</button>
                </div>
              )}
            </div>
            {/* Action button — same element for offer and non-offer.
                `min-h-10` rather than a fixed `h-10`: at two columns on a phone
                the card is ~140px wide, so «ΠΡΟΣΘΗΚΗ ΣΤΟ ΚΑΛΑΘΙ» ALWAYS wraps to
                two lines and a hard 40px left it cramped against the edges. It
                now grows instead, with real horizontal padding so the label
                never touches the sides, and `text-balance` so the two lines
                split evenly instead of leaving one orphan word.
                Unchanged on desktop: one line + py-2 measures under 40px, so
                `min-h-10` still resolves to exactly the old height. */}
            <button
              onClick={isOffer ? onOfferClick : onAddToCart}
              disabled={!isOffer && qtyPending}
              // Below 360px (two ~91px columns) «ΕΝΗΜΕΡΩΣΗ» alone measured 79px
              // at 12px — wider than the 67px inside px-3, so it sat 6px from
              // the edges. 11px + px-2 gives it real side room there; every
              // wider screen is unchanged.
              className="@container w-full min-h-10 flex items-center justify-center gap-2 px-3 max-[359px]:px-2 py-2 bg-brand hover:bg-brand-hover disabled:opacity-50 disabled:hover:bg-brand disabled:cursor-wait text-brand-text text-[12px] max-[359px]:text-[11px] font-gotham font-bold leading-[1.3] transition-colors cursor-pointer"
            >
              {/* One rule across every variant of this card: a basket means the
                  button ADDS TO THE CART. The offer button opens a popup and
                  adds nothing, so it carries no glyph — the red
                  «ΣΥΜΜΕΤΕΧΕΙ ΣΕ ΠΡΟΣΦΟΡΕΣ» label above marks it instead. */}
              {/* Narrow button: no glyph. At two columns on a phone the button is
                  ~145px, and the icon pushed «ΠΡΟΣΘΗΚΗ ΣΤΟ ΚΑΛΑΘΙ» onto a third
                  line — the label alone reads better (founder's call). The rule
                  is the BUTTON's own width (container query), not the screen's:
                  the home grid at 1024px has 107px buttons, where the glyph left
                  «ΕΝΗΜΕΡΩΣΗ» 2px from the edge. From 170px up the basket returns,
                  where it costs nothing and says "this one adds to the cart". */}
              {!isOffer && (
                <span className="hidden @[170px]:inline-flex" aria-hidden>
                  {cartAdded ? <CheckIcon /> : <BasketIcon />}
                </span>
              )}
              <span className="text-center text-balance">
                {isOffer ? CHOOSE_OFFER_LABEL : cartAdded ? doneLabel : actionLabel}
              </span>
            </button>
          </>
        )}
      </div>
      {popup}
    </div>
  );
}
